import { useEffect, useRef, useState } from 'react';

import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { Maybe } from '@/lib/types';

import { VideoPlayer } from './components';

import * as S from './Video.styles';
interface VideoProps {
  controls?: boolean;
  desktopAspectRatio?: { x: number; y: number };
  mediaUrl: Maybe<string>;
  mobileAspectRatio?: { x: number; y: number };
  poster?: string;
  setLoadingState?: (state: string) => void;
  title?: Maybe<string>;
  videoPlayOption?: string | null;
}
export const Video = ({
  controls = true,
  desktopAspectRatio,
  mediaUrl,
  mobileAspectRatio,
  poster,
  setLoadingState,
  title,
  videoPlayOption = 'AutoplayLoop',
}: VideoProps) => {
  const { sendEvent } = useGoogleAnalytics();
  const [autoPlay, setAutoPlay] = useState<boolean>(true);
  const [loop, setLoop] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [percent, setPercent] = useState(0);
  const [openFullVideo, setOpenFullVideo] = useState<boolean>(false);
  const [showPauseButton, setShowPauseButton] = useState<boolean>(false);

  useEffect(() => {
    switch (videoPlayOption) {
      case 'PlayOnClick':
        setAutoPlay(false);
        setLoop(false);
        break;
      case 'AutoplayOnce':
        setAutoPlay(true);
        setLoop(false);
        break;
      case 'AutoplayLoop':
        setAutoPlay(true);
        setLoop(true);
        break;
      case 'OpenPopup':
        setAutoPlay(false);
        setLoop(false);
        break;
      default:
        break;
    }
  }, [videoPlayOption]);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.load();
  }, [mediaUrl]);

  useEffect(() => {
    let hideControlsTimeout: NodeJS.Timeout;

    const handleMouseMove = () => {
      setShowPauseButton(true);
      clearTimeout(hideControlsTimeout);

      hideControlsTimeout = setTimeout(
        () => {
          setShowPauseButton(false);
        },
        videoRef?.current?.currentTime === 0 || videoRef?.current?.ended
          ? 0
          : 500,
      );
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    setLoadingState?.('loaded');
    if (!video) {
      return;
    }
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(video.duration);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (videoPlayOption === 'OpenPopup') {
      sendEvent({
        event: EVENT_NAME.VIDEO_START,
        video_title: title as string,
        video_url: mediaUrl as string,
      });
      setOpenFullVideo(true);
      return;
    }
    if (video?.paused) {
      video.play();
      if (percent === 0 || percent === 100) {
        sendEvent({
          event: EVENT_NAME.VIDEO_START,
          video_title: title as string,
          video_url: mediaUrl as string,
        });
      }
    } else {
      video?.pause();
    }
  };

  useEffect(() => {
    setPercent(Math.floor((currentTime / duration) * 100));
  }, [duration, currentTime]);

  useEffect(() => {
    if (!autoPlay && !loop) {
      const videoPercents = [25, 50, 75];
      if (videoPercents?.includes(percent)) {
        sendEvent({
          event: EVENT_NAME.VIDEO_PROGRESS,
          video_title: title as string,
          video_url: mediaUrl as string,
          video_percent: percent,
        });
      }
      if (percent === 100) {
        sendEvent({
          event: EVENT_NAME.VIDEO_COMPLETE,
          video_title: title as string,
        });
      }
    }
  }, [percent]);

  const onClose = () => {
    setOpenFullVideo(false);
  };
  const handlePlay = () => {
    setLoadingState?.('loaded');
  };
  return (
    <>
      {mediaUrl && (
        <S.VideoBox
          $currentTime={videoRef?.current?.currentTime}
          $isEnded={videoRef?.current?.ended}
        >
          <S.PlayerVideo
            ref={videoRef}
            $desktopAspectRatio={desktopAspectRatio}
            $mobileAspectRatio={mobileAspectRatio}
            autoPlay={autoPlay}
            controls={controls}
            onCanPlay={handlePlay}
            loop={loop}
            muted
            playsInline
            poster={poster}
          >
            <source src={mediaUrl} type='video/mp4' />
            <source src={mediaUrl} type='video/webm' />
          </S.PlayerVideo>
          <S.VideoActionButton
            aria-label='Play video'
            className='videoPlayButton'
            data-testid='videoPlayButton'
            onClick={togglePlayPause}
          >
            {!(autoPlay && loop) &&
              (videoRef?.current?.paused ? (
                <S.VideoPlayIcon data-testid='videoPlayButtonIcon' />
              ) : (
                showPauseButton && (
                  <S.VideoPauseIcon data-testid='videoPauseButtonIcon' />
                )
              ))}
          </S.VideoActionButton>
        </S.VideoBox>
      )}
      {openFullVideo && (
        <VideoPlayer
          data-testid='videoPopupModal'
          mediaUrl={mediaUrl}
          onClose={onClose}
          poster={poster}
          title={title}
        />
      )}
    </>
  );
};
